import React from 'react';
import { Box, Chip, Typography, Button } from '@mui/material';
import { Visibility as ViewAllIcon } from '@mui/icons-material';

/**
 * Image stage labels
 */
const STAGE_LABELS = {
    INITIAL: { label: 'Initial', color: 'primary' },
    IN_PROGRESS: { label: 'In Progress', color: 'warning' },
    COMPLETED: { label: 'Completed', color: 'success' },
};

/**
 * ImageThumbnailGrid - Displays a read-only grid of image thumbnails.
 * Shows up to maxVisible images, with "View All" button for more.
 * 
 * @param {Array} images - Array of image objects { id, url, thumbnailUrl, stage, uploadedAt }
 * @param {function} onImageClick - Callback when an image is clicked (index)
 * @param {function} onViewAllClick - Callback when "View All" button is clicked
 * @param {number} maxVisible - Maximum images to show before "View All" (default: 6)
 * @param {boolean} showStageLabels - Whether to show stage labels (default: true)
 */
export default function ImageThumbnailGrid({
    images = [],
    onImageClick,
    onViewAllClick,
    maxVisible = 6,
    showStageLabels = true,
}) {
    if (images.length === 0) {
        return null;
    }

    const visibleImages = images.slice(0, maxVisible);
    const hasMore = images.length > maxVisible;
    const remainingCount = images.length - maxVisible;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                    Photos ({images.length})
                </Typography>
                {hasMore && (
                    <Button
                        size="small"
                        startIcon={<ViewAllIcon />}
                        onClick={onViewAllClick}
                    >
                        View All
                    </Button>
                )}
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                    gap: 1,
                }}
            >
                {visibleImages.map((image, index) => (
                    <Box
                        key={image.id || index}
                        sx={{
                            position: 'relative',
                            paddingTop: '100%', // 1:1 aspect ratio
                            borderRadius: 1,
                            overflow: 'hidden',
                            bgcolor: 'grey.200',
                        }}
                    >
                        <Box
                            component="img"
                            src={image.thumbnailUrl || image.url}
                            alt={`Photo ${index + 1}`}
                            onClick={() => onImageClick?.(index)}
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                cursor: 'pointer',
                            }}
                        />

                        {/* Stage Label */}
                        {showStageLabels && image.stage && STAGE_LABELS[image.stage] && (
                            <Chip
                                label={STAGE_LABELS[image.stage].label}
                                color={STAGE_LABELS[image.stage].color}
                                size="small"
                                sx={{
                                    position: 'absolute',
                                    top: 4,
                                    left: 4,
                                    fontSize: '0.65rem',
                                    height: 20,
                                    '& .MuiChip-label': {
                                        px: 0.75,
                                    },
                                }}
                            />
                        )}
                    </Box>
                ))}

                {/* View More Indicator */}
                {hasMore && (
                    <Box
                        onClick={onViewAllClick}
                        sx={{
                            position: 'relative',
                            paddingTop: '100%',
                            borderRadius: 1,
                            overflow: 'hidden',
                            bgcolor: 'grey.300',
                            cursor: 'pointer',
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                            }}
                        >
                            <Typography variant="h6" color="text.secondary">
                                +{remainingCount}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                more
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
